import React, { useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { UtensilsCrossed, PlusCircle, Search, ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getRecipes } from '../../services/recipe';
import { useDebounce } from '../../hooks/useDebounce';
import { ThemeSwitcher } from './ThemeSwitcher';

export default function Navbar() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = React.useState(searchParams.get('search') || '');
  const [selectedTag, setSelectedTag] = React.useState(searchParams.get('tag') || '');
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Get all unique tags from recipes
  const { data: recipes = [] } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => getRecipes(),
  });

  const uniqueTags = React.useMemo(() => {
    const tags = new Set<string>();
    recipes.forEach(recipe => {
      recipe.dietaryTags.forEach(tag => tags.add(tag.toLowerCase()));
    });
    return Array.from(tags).sort();
  }, [recipes]);

  // Update URL when search changes
  React.useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      newSearchParams.set('search', debouncedSearch);
    } else {
      newSearchParams.delete('search');
    }
    if (selectedTag) {
      newSearchParams.set('tag', selectedTag);
    } else {
      newSearchParams.delete('tag');
    }
    setSearchParams(newSearchParams);
  }, [debouncedSearch, selectedTag, setSearchParams]);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // If we're not on the home page, navigate there
    if (window.location.pathname !== '/') {
      const params = new URLSearchParams();
      if (value) params.set('search', value);
      if (selectedTag) params.set('tag', selectedTag);
      navigate('/?' + params.toString());
    }
  }, [navigate, selectedTag]);

  const handleTagChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedTag(value);
    
    // If we're not on the home page, navigate there
    if (window.location.pathname !== '/') {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (value) params.set('tag', value);
      navigate('/?' + params.toString());
    }
  }, [navigate, searchTerm]);

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 transition-all duration-300">
      <div className="container mx-auto px-4">
        <div className="flex flex-wrap items-center py-4 gap-4">
          <div className="flex items-center justify-between w-full md:w-auto">
            <Link to="/" className="flex items-center space-x-2">
              <UtensilsCrossed className="h-6 w-6 text-indigo-600" />
              <span className="text-xl font-semibold text-gray-900">Ezboss Recipe</span>
            </Link>
          </div>

          <div className="w-full md:flex-1 md:max-w-3xl order-last md:order-none">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1 min-w-0">
                <input
                  type="search"
                  value={searchTerm}
                  onChange={handleSearch}
                  placeholder="Search recipes..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg sm:rounded-r-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-[44px]"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
              <div className="relative sm:inline-block">
                <div className="relative">
                  <select
                    value={selectedTag}
                    onChange={handleTagChange}
                    className="w-full sm:w-auto h-[44px] pl-4 pr-8 border border-gray-200 sm:border-l-0 rounded-lg sm:rounded-l-none appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent cursor-pointer min-w-[120px]"
                  >
                    <option value="">All Tags</option>
                    {uniqueTags.map(tag => (
                      <option key={tag} value={tag}>
                        {tag}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 ml-auto">
            <ThemeSwitcher />
            <Link
              to="/recipes/new"
              className="inline-flex items-center px-3 sm:px-4 py-2 min-h-[44px] border border-transparent rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm whitespace-nowrap"
            >
              <PlusCircle className="h-5 w-5 mr-2" />
              <span className="hidden sm:inline">New Recipe</span>
              <span className="sm:hidden">New</span>
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}